import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Skeleton,
  MenuItem,
  Link,
  Button,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { marketService, KiteStatus, Instrument, BulkQuoteData, OptionExpiry, OptionsWatchlist } from '../services/market.service';
import { ALL_SYMBOLS, LOT_SIZE_MAP } from '../utils/constants';

// ============================================================================
// CONSTANTS & COLORS
// ============================================================================

// "The Session Board" palette — a serious desk, not a retail app.
const COLORS = {
  graphite: '#14181F',   // ink + the single dark element (Session Spine rail)
  primary: '#14181F',
  primaryLight: '#3A414D',
  primaryDark: '#0B0E13',
  positive: '#067A55',   // Rise — deep serious emerald (was candy #10B981)
  positiveLight: '#E3F1EC', // rise tick-flash wash
  negativeDark: '#A22A24',
  negative: '#C6362F',   // Fall — brick vermilion (was fire-engine #EF4444)
  negativeLight: '#F7E6E4', // fall tick-flash wash
  warning: '#E0A100',    // Live — amber, ONLY the open-market pulse / now-marker
  live: '#E0A100',
  warningLight: '#FBF0D6',
  blue: '#3A414D',       // demoted (exchange chip) — no more bright blue accent
  blueLight: '#EEF0F3',
  pageBg: '#FBFBFC',     // Paper (kills the old purple-tinted #F8F7FF)
  cardBg: '#FFFFFF',
  tableBg: '#FFFFFF',
  tableHeaderBg: '#F7F8FA',
  textPrimary: '#14181F',  // Graphite ink (was indigo #1E1B4B — the purple tint)
  textSecondary: '#6B7280',
  border: '#E7E9EE',
};

// Deliberate, non-Roboto type roles.
const MONO = "'Geist Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace"; // every number
const DISPLAY = "'Saira Semi Condensed', 'Inter', sans-serif";                 // eyebrows / index labels

const POLL_INTERVAL = 2000; // 2 seconds (1s DynamoDB flush + 2s poll = ~1.5s avg latency)

// Default underlyings shown while API loads
const DEFAULT_OPTION_UNDERLYINGS = ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY'];

type Segment = 'equity' | 'futures' | 'options';

type SortField = 'tradingsymbol' | 'ltp' | 'change' | 'changePercent' | 'open' | 'high' | 'low' | 'close' | 'volume' | 'oi' | 'lot_size' | 'expiry' | 'strike';

// ============================================================================
// FORMAT UTILITIES
// ============================================================================

const formatPrice = (val: number): string =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);

const formatChange = (val: number): string =>
  `${val >= 0 ? '+' : ''}${new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val)}`;

const formatPercent = (val: number): string =>
  `${val >= 0 ? '+' : ''}${val.toFixed(2)}%`;

const formatVolume = (val: number): string => {
  if (val >= 10000000) return `${(val / 10000000).toFixed(2)}Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(2)}L`;
  if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
  return val.toLocaleString('en-IN');
};

const formatNumber = (val: number): string => new Intl.NumberFormat('en-IN').format(val);

// ============================================================================
// IST CLOCK HOOK
// ============================================================================

function useISTClock() {
  const [time, setTime] = useState('');

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const ist = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
      const h = ist.getHours().toString().padStart(2, '0');
      const m = ist.getMinutes().toString().padStart(2, '0');
      const s = ist.getSeconds().toString().padStart(2, '0');
      setTime(`${h}:${m}:${s} IST`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return time;
}

// ============================================================================
// MARKET STATUS HELPER
// ============================================================================

function getMarketStatus(): { label: string; color: string } {
  const now = new Date();
  const ist = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const h = ist.getHours();
  const m = ist.getMinutes();
  const day = ist.getDay();
  const mins = h * 60 + m;

  // Weekend
  if (day === 0 || day === 6) return { label: 'Closed', color: COLORS.negative };
  // Pre-market: 9:00 - 9:15
  if (mins >= 540 && mins < 555) return { label: 'Pre-Open', color: COLORS.warning };
  // Market open: 9:15 - 15:30
  if (mins >= 555 && mins < 930) return { label: 'Open', color: COLORS.positive };
  // Post-market: 15:30 - 16:00
  if (mins >= 930 && mins < 960) return { label: 'Post-Market', color: COLORS.warning };

  return { label: 'Closed', color: COLORS.negative };
}

// ============================================================================
// BUILD LOCAL INSTRUMENTS FROM CONSTANTS (fallback when API unavailable)
// ============================================================================

function buildLocalInstruments(): Instrument[] {
  return ALL_SYMBOLS.map((sym, idx) => ({
    tradingsymbol: sym,
    instrument_token: 100000 + idx,
    exchange: 'NSE',
    instrument_type: 'EQ',
    name: sym,
    lot_size: LOT_SIZE_MAP[sym] || 1,
    segment: 'NSE',
  }));
}

// ============================================================================
// SKELETON TABLE ROW
// ============================================================================

function SkeletonRows({ cols, rows }: { cols: number; rows: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <TableRow key={r}>
          {Array.from({ length: cols }).map((_, c) => (
            <TableCell key={c}>
              <Skeleton variant="text" width={c === 0 ? 120 : 80} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

// ============================================================================
// REDUCED MOTION
// ============================================================================

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduce(mq.matches);
    const h = () => setReduce(mq.matches);
    mq.addEventListener?.('change', h);
    return () => mq.removeEventListener?.('change', h);
  }, []);
  return reduce;
}

// ============================================================================
// INDEX PULSE — a single index as a large tabular-mono figure
// ============================================================================

function IndexPulse({ label, quote }: { label: string; quote: BulkQuoteData | null }) {
  const has = !!quote && quote.ltp > 0;
  const up = (quote?.change ?? 0) >= 0;
  const c = up ? COLORS.positive : COLORS.negative;
  return (
    <Box sx={{ minWidth: 190 }}>
      <Typography sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.09em', color: COLORS.textSecondary }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.25 }}>
        <Typography sx={{ fontFamily: MONO, fontWeight: 600, fontSize: { xs: '1.85rem', md: '2.35rem' }, lineHeight: 1, color: COLORS.graphite, fontFeatureSettings: '"tnum"' }}>
          {has ? new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(quote!.ltp) : '—'}
        </Typography>
        {has && (up ? <TrendingUpIcon sx={{ fontSize: 22, color: c }} /> : <TrendingDownIcon sx={{ fontSize: 22, color: c }} />)}
      </Box>
      {has && (
        <Typography sx={{ fontFamily: MONO, fontSize: '0.8rem', fontWeight: 500, color: c, mt: 0.4, fontFeatureSettings: '"tnum"' }}>
          {`${up ? '+' : ''}${quote!.change.toFixed(2)}`}&nbsp;&nbsp;{`${up ? '+' : ''}${quote!.changePercent.toFixed(2)}%`}
        </Typography>
      )}
    </Box>
  );
}

// ============================================================================
// SESSION SPINE — the signature: a live scale of the NSE trading day
// ============================================================================

const OPEN_START = 555;    // 09:15
const OPEN_END = 930;      // 15:30
const CLOSING_FROM = 900;  // 15:00 — the last 30 minutes

function sessionState() {
  const ist = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const mins = ist.getHours() * 60 + ist.getMinutes() + ist.getSeconds() / 60;
  const weekend = ist.getDay() === 0 || ist.getDay() === 6;
  const pos = Math.max(0, Math.min(1, (mins - OPEN_START) / (OPEN_END - OPEN_START)));
  const dur = (m: number) => { const h = Math.floor(m / 60); const mm = Math.round(m % 60); return h > 0 ? `${h}h ${mm}m` : `${mm}m`; };
  if (weekend) return { phase: 'Closed', live: false, pos: 0, note: 'Opens Mon 09:15' };
  if (mins < 540) return { phase: 'Closed', live: false, pos: 0, note: `Opens in ${dur(555 - mins)}` };
  if (mins < OPEN_START) return { phase: 'Pre-open', live: false, pos: 0, note: `Opens in ${dur(555 - mins)}` };
  if (mins < CLOSING_FROM) return { phase: 'Open', live: true, pos, note: `${dur(OPEN_END - mins)} to close` };
  if (mins < OPEN_END) return { phase: 'Closing hour', live: true, pos, note: `${dur(OPEN_END - mins)} to close` };
  if (mins < 960) return { phase: 'Post-market', live: false, pos: 1, note: 'Closed at 15:30' };
  return { phase: 'Closed', live: false, pos: 1, note: 'Opens tomorrow 09:15' };
}

function SessionSpine() {
  const s = sessionState();
  const reduce = usePrefersReducedMotion();
  const closingPct = ((CLOSING_FROM - OPEN_START) / (OPEN_END - OPEN_START)) * 100;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', color: COLORS.textSecondary }}>09:15</Typography>
      <Box sx={{ position: 'relative', flex: 1, minWidth: 160, height: 20, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ position: 'absolute', left: 0, right: 0, height: 2, bgcolor: COLORS.border, borderRadius: 1 }} />
        <Box sx={{ position: 'absolute', left: 0, width: `${s.pos * 100}%`, height: 2, bgcolor: COLORS.graphite, borderRadius: 1 }} />
        {/* closing-hour tick (15:00) */}
        <Box sx={{ position: 'absolute', left: `${closingPct}%`, top: 2, width: '1px', height: 9, bgcolor: COLORS.border }} />
        {(s.live || s.pos > 0) && (
          <Box sx={{
            position: 'absolute', left: `${s.pos * 100}%`, transform: 'translateX(-50%)',
            width: 10, height: 10, borderRadius: '50%',
            bgcolor: s.live ? COLORS.live : COLORS.graphite,
            boxShadow: s.live ? `0 0 0 3px ${COLORS.live}33` : 'none',
            animation: s.live && !reduce ? 'sessionPulse 2s ease-in-out infinite' : 'none',
          }} />
        )}
      </Box>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', color: COLORS.textSecondary }}>15:30</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 0.5 }}>
        <Box sx={{
          px: 0.9, py: 0.2, borderRadius: '4px',
          bgcolor: s.live ? `${COLORS.live}22` : '#EEF0F3',
          fontFamily: DISPLAY, fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em',
          color: s.live ? '#8A6400' : COLORS.textSecondary,
        }}>{s.phase}</Box>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', color: COLORS.graphite, fontWeight: 500 }}>{s.note}</Typography>
      </Box>
    </Box>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function MarketData() {
  const istClock = useISTClock();
  const marketStatus = getMarketStatus();

  // ---------- State ----------
  const [segment, setSegment] = useState<Segment>('equity');
  const [equityFilter, setEquityFilter] = useState<'stocks' | 'all'>('stocks');
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [quotes, setQuotes] = useState<Record<string, BulkQuoteData>>({});
  const [prevQuotes, setPrevQuotes] = useState<Record<string, BulkQuoteData>>({});
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('tradingsymbol');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [kiteStatus, setKiteStatus] = useState<KiteStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [, setLastRefresh] = useState<Date | null>(null);

  // Options-specific state
  const [optionUnderlying, setOptionUnderlying] = useState('NIFTY');
  const [optionExpiry, setOptionExpiry] = useState('');
  const [fnoUnderlyings, setFnoUnderlyings] = useState<string[]>(DEFAULT_OPTION_UNDERLYINGS);

  // ---------- Options Watchlist generator ----------
  const [wlExpiries, setWlExpiries] = useState<OptionExpiry[]>([]);
  const [wlExpiry, setWlExpiry] = useState('');
  const [wlResult, setWlResult] = useState<OptionsWatchlist | null>(null);
  const [wlFormat, setWlFormat] = useState<'txt' | 'csv'>('txt');
  const [wlLoading, setWlLoading] = useState(false);
  const [wlError, setWlError] = useState<string | null>(null);
  const [wlCopied, setWlCopied] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const flashTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // ---------- Fetch Kite Status ----------
  const fetchKiteStatus = useCallback(async () => {
    try {
      const status = await marketService.getKiteStatus();
      setKiteStatus(status);
    } catch {
      setKiteStatus(null);
    }
  }, []);

  // ---------- Fetch Instruments ----------
  const fetchInstruments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const exchangeMap: Record<Segment, string> = { equity: 'NSE', futures: 'NFO', options: 'NFO' };
      const segmentMap: Record<Segment, string> = { equity: '', futures: 'FUT', options: '' }; // Options: no segment filter, use underlying
      const params: { exchange: string; segment?: string; underlying?: string } = {
        exchange: exchangeMap[segment],
        segment: segmentMap[segment] || undefined,
      };
      // For options, pass underlying to get CE+PE for that scrip
      if (segment === 'options') {
        params.underlying = optionUnderlying;
      }
      const result = await marketService.getInstruments(params);
      setInstruments(result.instruments);
    } catch {
      // Fallback: build local instruments for equity
      if (segment === 'equity') {
        setInstruments(buildLocalInstruments());
      } else {
        setInstruments([]);
      }
    } finally {
      setLoading(false);
    }
  }, [segment, optionUnderlying]); // Refetch when segment or underlying changes

  // ---------- Fetch Quotes ----------
  const fetchQuotes = useCallback(async (symbolList: string[]) => {
    if (symbolList.length === 0) return;
    try {
      // Add exchange prefix for Kite REST API (equity = NSE:, F&O = NFO:)
      const prefixed = symbolList.map((s) => {
        if (s.includes(':')) return s; // already prefixed
        if (segment === 'futures' || segment === 'options') return `NFO:${s}`;
        return `NSE:${s}`;
      });
      const result = await marketService.getBulkQuotes(prefixed);
      // Re-key quotes by plain symbol (remove exchange prefix)
      const reKeyed: Record<string, BulkQuoteData> = {};
      for (const [key, val] of Object.entries(result.quotes)) {
        const plainSym = key.includes(':') ? key.split(':')[1] : key;
        reKeyed[val.tradingsymbol || plainSym] = val;
      }
      setPrevQuotes((prev) => ({ ...prev, ...quotes }));
      setQuotes((prev) => ({ ...prev, ...reKeyed }));
      setLastRefresh(new Date());
    } catch {
      // If bulk quotes fail, try regular quotes as fallback (DynamoDB → Yahoo)
      try {
        const result = await marketService.getQuotes(symbolList);
        const mapped: Record<string, BulkQuoteData> = {};
        for (const [sym, q] of Object.entries(result.prices)) {
          mapped[sym] = {
            tradingsymbol: sym,
            ltp: q.price,
            open: 0,
            high: 0,
            low: 0,
            close: q.price - q.change,
            change: q.change,
            changePercent: q.changePercent,
            volume: 0,
            lastTradeTime: q.lastUpdated,
          };
        }
        setPrevQuotes((prev) => ({ ...prev, ...quotes }));
        setQuotes((prev) => ({ ...prev, ...mapped }));
        setLastRefresh(new Date());
      } catch {
        // Silently fail - quotes will show as --
      }
    }
  }, [quotes, segment]);

  // ---------- Filtered + Sorted Instruments ----------
  const filteredInstruments = useMemo(() => {
    let items = [...instruments];

    // Equity sub-filter: "stocks" = real tradeable stocks only, "all" = everything
    if (segment === 'equity' && equityFilter === 'stocks') {
      // SM=SME, ST=SME Trade, BE=Book Entry, GB=Gold Bond, BZ=Buyback, SF=Settlement,
      // GS=Govt Sec, IV=InvIT, RR=Rights Renunciation, RL=Rights Lapse, NB=NCD Bond,
      // NO/NG=NCD, E1=ETF series, Y1=Yield series, PP=Partly Paid, BL/IL=Bond series
      const bondSuffix = /-(SM|ST|BE|GB|BZ|SF|GS|IV|RR|RL|NB|NO|NG|PP|BL|IL|SG|W\d|N\d|E\d|Y\d)$/;
      items = items.filter((inst) => {
        const sym = inst.tradingsymbol;
        if (inst.segment === 'INDICES' || inst.segment === 'NSE-INDICES') return true; // always keep indices
        // Bonds/NCDs/debentures start with a digit — no real equity does
        if (/^\d/.test(sym)) return false;
        if (inst.lot_size > 1) return false;           // bonds have lot_size 100+
        if (inst.name && inst.name.includes('%')) return false; // SDL/bond names
        if (bondSuffix.test(sym)) return false;
        return true;
      });
    }

    // For options, filter by expiry (underlying is already filtered server-side)
    if (segment === 'options' && optionExpiry) {
      items = items.filter((inst) => inst.expiry === optionExpiry);
    }

    // Search filter — symbol code only, sorted by relevance (exact > starts-with > contains)
    if (search) {
      const q = search.toUpperCase();
      items = items.filter((inst) =>
        inst.tradingsymbol.toUpperCase().includes(q)
      );
      // Sort by relevance: exact match first, then starts-with (+ space), then starts-with, then contains
      items.sort((a, b) => {
        const aS = a.tradingsymbol.toUpperCase();
        const bS = b.tradingsymbol.toUpperCase();
        // Priority 0: exact match (e.g. "NIFTY" === "NIFTY")
        const aExact = aS === q ? 0 : 1;
        const bExact = bS === q ? 0 : 1;
        if (aExact !== bExact) return aExact - bExact;
        // Priority 1: starts with query + space (e.g. "NIFTY 50" for query "NIFTY")
        const aStartsSpace = aS.startsWith(q + ' ') ? 0 : 1;
        const bStartsSpace = bS.startsWith(q + ' ') ? 0 : 1;
        if (aStartsSpace !== bStartsSpace) return aStartsSpace - bStartsSpace;
        // Priority 2: starts with query (e.g. "NIFTYETF" for query "NIFTY")
        const aStarts = aS.startsWith(q) ? 0 : 1;
        const bStarts = bS.startsWith(q) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        // Priority 3: shorter symbols first
        return aS.length - bS.length;
      });
    }

    // Sort (only when not searching — search has its own relevance sort)
    if (!search) items.sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';

      switch (sortField) {
        case 'tradingsymbol':
          aVal = a.tradingsymbol;
          bVal = b.tradingsymbol;
          break;
        case 'ltp':
          aVal = quotes[a.tradingsymbol]?.ltp ?? 0;
          bVal = quotes[b.tradingsymbol]?.ltp ?? 0;
          break;
        case 'change':
          aVal = quotes[a.tradingsymbol]?.change ?? 0;
          bVal = quotes[b.tradingsymbol]?.change ?? 0;
          break;
        case 'changePercent':
          aVal = quotes[a.tradingsymbol]?.changePercent ?? 0;
          bVal = quotes[b.tradingsymbol]?.changePercent ?? 0;
          break;
        case 'volume':
          aVal = quotes[a.tradingsymbol]?.volume ?? 0;
          bVal = quotes[b.tradingsymbol]?.volume ?? 0;
          break;
        case 'oi':
          aVal = quotes[a.tradingsymbol]?.oi ?? 0;
          bVal = quotes[b.tradingsymbol]?.oi ?? 0;
          break;
        case 'lot_size':
          aVal = a.lot_size;
          bVal = b.lot_size;
          break;
        case 'expiry':
          aVal = a.expiry || '';
          bVal = b.expiry || '';
          break;
        case 'strike':
          aVal = a.strike || 0;
          bVal = b.strike || 0;
          break;
        default:
          aVal = quotes[a.tradingsymbol]?.[sortField as keyof BulkQuoteData] as number ?? 0;
          bVal = quotes[b.tradingsymbol]?.[sortField as keyof BulkQuoteData] as number ?? 0;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });

    return items;
  }, [instruments, search, sortField, sortDir, quotes, segment, equityFilter, optionUnderlying, optionExpiry]);

  // Paginated instruments
  const pagedInstruments = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredInstruments.slice(start, start + rowsPerPage);
  }, [filteredInstruments, page, rowsPerPage]);

  // Visible symbols for quote polling
  const visibleSymbols = useMemo(() => pagedInstruments.map((i) => i.tradingsymbol), [pagedInstruments]);

  // Available expiries for options (filtered by selected underlying)
  const availableExpiries = useMemo(() => {
    if (segment !== 'options') return [];
    const filtered = instruments.filter((i) =>
      i.expiry && (i.name === optionUnderlying || i.tradingsymbol.startsWith(optionUnderlying))
    );
    const expiries = [...new Set(filtered.map((i) => i.expiry!))].sort();
    return expiries;
  }, [instruments, segment, optionUnderlying]);

  // NIFTY and BANKNIFTY quote data for summary cards
  const niftyQuote = quotes['NIFTY'] || quotes['NIFTY 50'] || null;
  const bankNiftyQuote = quotes['BANKNIFTY'] || quotes['NIFTY BANK'] || null;

  // ---------- Effects ----------

  // Fetch kite status on mount
  useEffect(() => {
    fetchKiteStatus();
  }, [fetchKiteStatus]);

  // Fetch instruments when segment or options underlying changes
  useEffect(() => {
    setPage(0);
    fetchInstruments();
  }, [segment, optionUnderlying]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch F&O underlyings for Options tab
  useEffect(() => {
    if (segment !== 'options' || fnoUnderlyings.length > DEFAULT_OPTION_UNDERLYINGS.length) return;
    marketService.getFnOScrips().then(({ fnoScrips }) => {
      const symbols = fnoScrips.map(s => s.symbol).sort();
      if (symbols.length > 0) setFnoUnderlyings(symbols);
    }).catch(() => {}); // keep defaults on error
  }, [segment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load option expiries once for the watchlist generator (default to soonest
  // expiry that has the full F&O universe — the monthly with the most names).
  useEffect(() => {
    marketService.getOptionExpiries().then(({ expiries }) => {
      setWlExpiries(expiries);
      if (expiries.length && !wlExpiry) {
        const best = [...expiries].sort((a, b) => b.underlyingCount - a.underlyingCount)[0];
        setWlExpiry(best.expiry);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerateWatchlist = useCallback(async () => {
    if (!wlExpiry) return;
    setWlLoading(true);
    setWlError(null);
    setWlResult(null);
    setWlCopied(false);
    try {
      const result = await marketService.getOptionsWatchlist(wlExpiry);
      setWlResult(result);
    } catch (e: any) {
      setWlError(e?.response?.data?.error || e?.message || 'Failed to generate watchlist');
    } finally {
      setWlLoading(false);
    }
  }, [wlExpiry]);

  const handleCopyWatchlist = useCallback(async () => {
    if (!wlResult) return;
    const text = wlFormat === 'txt' ? wlResult.txt : wlResult.csv;
    try {
      await navigator.clipboard.writeText(text);
      setWlCopied(true);
      setTimeout(() => setWlCopied(false), 2000);
    } catch { /* clipboard blocked — user can select manually */ }
  }, [wlResult, wlFormat]);

  // Fetch quotes when visible instruments change
  useEffect(() => {
    if (visibleSymbols.length === 0) return;

    // Always try to fetch index quotes alongside visible
    const symbolsToFetch = [...new Set([...visibleSymbols, 'NIFTY', 'BANKNIFTY'])];
    fetchQuotes(symbolsToFetch);

    // Set up polling
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      const syms = [...new Set([...visibleSymbols, 'NIFTY', 'BANKNIFTY'])];
      fetchQuotes(syms);
    }, POLL_INTERVAL);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [visibleSymbols]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup flash timers
  useEffect(() => {
    return () => {
      Object.values(flashTimers.current).forEach(clearTimeout);
    };
  }, []);

  // ---------- Handlers ----------

  const handleSegmentChange = (_: React.MouseEvent<HTMLElement>, newSegment: Segment | null) => {
    if (newSegment) {
      setSegment(newSegment);
      setSearch('');
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleRefresh = () => {
    fetchInstruments();
    if (visibleSymbols.length > 0) {
      fetchQuotes([...new Set([...visibleSymbols, 'NIFTY', 'BANKNIFTY'])]);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(0);
  };

  // ---------- Flash detection ----------
  const getFlashAnimation = (symbol: string): string | undefined => {
    const prev = prevQuotes[symbol];
    const curr = quotes[symbol];
    if (!prev || !curr) return undefined;
    if (curr.ltp > prev.ltp) return 'flashGreen 0.8s ease-out';
    if (curr.ltp < prev.ltp) return 'flashRed 0.8s ease-out';
    return undefined;
  };

  // ---------- Sort label helper ----------
  const renderSortLabel = (field: SortField, label: string, align?: 'left' | 'right') => (
    <TableCell
      align={align || 'right'}
      sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: COLORS.textSecondary, bgcolor: COLORS.tableHeaderBg, whiteSpace: 'nowrap' }}
    >
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  // ---------- Price cell helper ----------
  const priceCell = (val: number | undefined) => {
    if (val === undefined || val === 0) return <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>--</Typography>;
    return <Typography variant="body2" sx={{ fontFamily: MONO, fontWeight: 500 }}>{formatPrice(val)}</Typography>;
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  // Token valid = REST API works (for quotes). isConnected = WebSocket streaming (for real-time).
  // Market page only needs valid token for REST quotes. WebSocket may be off on weekends.
  const isKiteConnected = kiteStatus?.tokenValid ?? false;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: COLORS.pageBg,
        pb: 3,
        // Keyframe animations
        '@keyframes flashGreen': {
          '0%': { backgroundColor: COLORS.positiveLight },
          '100%': { backgroundColor: 'transparent' },
        },
        '@keyframes flashRed': {
          '0%': { backgroundColor: COLORS.negativeLight },
          '100%': { backgroundColor: 'transparent' },
        },
        '@keyframes sessionPulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.45 },
        },
      }}
    >
      {/* ================================================================ */}
      {/* HERO — Session Board: NSE eyebrow · live clock · twin pulse · spine */}
      {/* ================================================================ */}
      {(() => {
        const isOpen = marketStatus.label === 'Open' || marketStatus.label === 'Closing hour';
        return (
          <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: '18px', border: `1px solid ${COLORS.border}`, bgcolor: COLORS.cardBg, mb: 3 }}>
            {/* eyebrow + clock */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: '0.85rem', letterSpacing: '0.16em', color: COLORS.graphite }}>NSE</Typography>
                <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: isOpen ? COLORS.live : COLORS.textSecondary }} />
                <Typography sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.11em', textTransform: 'uppercase', color: isOpen ? '#8A6400' : COLORS.textSecondary }}>
                  {isOpen ? 'Live' : marketStatus.label}
                </Typography>
                <Tooltip title={isKiteConnected ? 'Kite connected — live prices' : 'Kite disconnected — prices paused'}>
                  <FiberManualRecordIcon sx={{ fontSize: 9, color: isKiteConnected ? COLORS.positive : COLORS.negative, ml: 0.5 }} />
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography component="span" sx={{ fontFamily: MONO, fontWeight: 500, fontSize: '0.85rem', color: COLORS.graphite, letterSpacing: '0.02em' }}>
                  {istClock}&nbsp;<Box component="span" sx={{ color: COLORS.textSecondary, fontSize: '0.7rem' }}>IST</Box>
                </Typography>
                <Tooltip title="Refresh">
                  <IconButton onClick={handleRefresh} size="small" sx={{ color: COLORS.graphite }}>
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* twin index pulse */}
            <Box sx={{ display: 'flex', gap: { xs: 3, md: 6 }, flexWrap: 'wrap', mt: 2 }}>
              <IndexPulse label="Nifty 50" quote={niftyQuote} />
              <IndexPulse label="Bank Nifty" quote={bankNiftyQuote} />
            </Box>

            {/* the signature */}
            <SessionSpine />
          </Paper>
        );
      })()}

      {/* ================================================================ */}
      {/* ALERT IF KITE NOT CONNECTED */}
      {/* ================================================================ */}
      {!isKiteConnected && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px' }}>
          Live prices are paused.{' '}
          <Link href={marketService.getKiteLoginUrl()} target="_blank" rel="noopener" sx={{ fontWeight: 600 }}>
            Reconnect to Kite
          </Link>{' '}
          to resume.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ================================================================ */}
      {/* OPTIONS WATCHLIST GENERATOR — OTM call (above) + put (below) */}
      {/* ================================================================ */}
      <Paper sx={{ p: 2.5, borderRadius: '16px', border: `1px solid ${COLORS.border}`, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.textPrimary }}>
              Options Watchlist
            </Typography>
            <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
              For every F&amp;O underlying: CALL just above spot, PUT just below spot. Copy and paste into a TradingView watchlist.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label="Expiry"
              value={wlExpiry}
              onChange={(e) => { setWlExpiry(e.target.value); setWlResult(null); }}
              sx={{ minWidth: 200 }}
            >
              {wlExpiries.length === 0 && <MenuItem value="">Loading…</MenuItem>}
              {wlExpiries.map((e) => (
                <MenuItem key={e.expiry} value={e.expiry}>
                  {e.expiry} ({e.underlyingCount} underlyings)
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              onClick={handleGenerateWatchlist}
              disabled={!wlExpiry || wlLoading}
              startIcon={wlLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : undefined}
              sx={{
                textTransform: 'none', bgcolor: '#0A0A0A', boxShadow: 'none',
                '&:hover': { bgcolor: '#262626', boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: '#E5E5E5', color: '#A3A3A3' },
              }}
            >
              {wlLoading ? 'Generating…' : 'Generate'}
            </Button>
          </Box>
        </Box>

        {wlError && (
          <Alert severity="warning" sx={{ borderRadius: '10px', mb: 1.5 }} onClose={() => setWlError(null)}>
            {wlError}
          </Alert>
        )}

        {wlResult && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`${wlResult.symbolCount} symbols · ${wlResult.underlyingCount} underlyings`}
                  size="small"
                  sx={{ bgcolor: '#F4F4F5', color: COLORS.textPrimary, fontWeight: 600, fontSize: '0.7rem' }}
                />
                {wlResult.skipped.length > 0 && (
                  <Tooltip title={wlResult.skipped.slice(0, 30).join('\n')}>
                    <Chip label={`${wlResult.skipped.length} skipped`} size="small" sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 600, fontSize: '0.7rem' }} />
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ToggleButtonGroup
                  value={wlFormat}
                  exclusive
                  size="small"
                  onChange={(_e, v) => { if (v) setWlFormat(v); }}
                >
                  <ToggleButton value="txt" sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.3 }}>TV symbols</ToggleButton>
                  <ToggleButton value="csv" sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.3 }}>CSV</ToggleButton>
                </ToggleButtonGroup>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCopyWatchlist}
                  sx={{
                    textTransform: 'none',
                    borderColor: wlCopied ? COLORS.positive : '#0A0A0A',
                    color: wlCopied ? COLORS.positive : '#0A0A0A',
                    '&:hover': { borderColor: '#262626', bgcolor: '#F4F4F5' },
                  }}
                >
                  {wlCopied ? 'Copied ✓' : 'Copy'}
                </Button>
              </Box>
            </Box>
            <TextField
              value={wlFormat === 'txt' ? wlResult.txt : wlResult.csv}
              multiline
              minRows={4}
              maxRows={10}
              fullWidth
              InputProps={{ readOnly: true, sx: { fontFamily: MONO, fontSize: '0.72rem' } }}
              onFocus={(e) => e.target.select()}
            />
          </Box>
        )}
      </Paper>

      {/* ================================================================ */}
      {/* SEGMENT TABS + SEARCH */}
      {/* ================================================================ */}
      <Paper sx={{ p: 2.5, borderRadius: '16px', border: `1px solid ${COLORS.border}`, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <ToggleButtonGroup
            value={segment}
            exclusive
            onChange={handleSegmentChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: 3,
                py: 0.8,
                fontWeight: 600,
                textTransform: 'none',
                borderColor: COLORS.border,
                '&.Mui-selected': {
                  bgcolor: `${COLORS.primary}15`,
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  '&:hover': { bgcolor: `${COLORS.primary}20` },
                },
              },
            }}
          >
            <ToggleButton value="equity">Equity</ToggleButton>
            <ToggleButton value="futures">Futures</ToggleButton>
            <ToggleButton value="options">Options</ToggleButton>
          </ToggleButtonGroup>

          {/* Equity sub-filter: Stocks vs All */}
          {segment === 'equity' && (
            <ToggleButtonGroup
              value={equityFilter}
              exclusive
              onChange={(_, v) => { if (v) { setEquityFilter(v); setPage(0); } }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  fontSize: '0.7rem',
                  px: 1.5,
                  py: 0.4,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderColor: COLORS.border,
                  '&.Mui-selected': {
                    bgcolor: `${COLORS.positive}15`,
                    color: COLORS.positive,
                    borderColor: COLORS.positive,
                    '&:hover': { bgcolor: `${COLORS.positive}20` },
                  },
                },
              }}
            >
              <ToggleButton value="stocks">Stocks</ToggleButton>
              <ToggleButton value="all">All ({instruments.length.toLocaleString()})</ToggleButton>
            </ToggleButtonGroup>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {segment !== 'options' && (
              <Typography sx={{ fontFamily: MONO, fontSize: '0.78rem', color: COLORS.textSecondary, whiteSpace: 'nowrap' }}>
                {formatNumber(filteredInstruments.length)}&nbsp;
                <Box component="span" sx={{ fontFamily: DISPLAY, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>instruments</Box>
              </Typography>
            )}
            {/* Options-specific filters */}
            {segment === 'options' && (
              <>
                <TextField
                  select
                  size="small"
                  label="Underlying"
                  value={optionUnderlying}
                  onChange={(e) => { setOptionUnderlying(e.target.value); setOptionExpiry(''); setPage(0); }}
                  sx={{ minWidth: 140 }}
                >
                  {fnoUnderlyings.map((u) => (
                    <MenuItem key={u} value={u}>{u}</MenuItem>
                  ))}
                </TextField>
                {availableExpiries.length > 0 && (
                  <TextField
                    select
                    size="small"
                    label="Expiry"
                    value={optionExpiry}
                    onChange={(e) => { setOptionExpiry(e.target.value); setPage(0); }}
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value="">All Expiries</MenuItem>
                    {availableExpiries.map((exp) => (
                      <MenuItem key={exp} value={exp}>{exp}</MenuItem>
                    ))}
                  </TextField>
                )}
              </>
            )}

            {/* Hide search bar on Options tab — Underlying + Expiry dropdowns are sufficient */}
            {segment !== 'options' && (
              <TextField
                size="small"
                placeholder="Search symbol..."
                value={search}
                onChange={handleSearchChange}
                sx={{ minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: COLORS.textSecondary, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>
        </Box>
      </Paper>

      {/* ================================================================ */}
      {/* DATA TABLE */}
      {/* ================================================================ */}
      <Paper sx={{ borderRadius: '16px', border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
        {segment === 'options' ? (
          // ---- OPTIONS: OPTION CHAIN LAYOUT ----
          <OptionChainTable
            instruments={pagedInstruments}
            quotes={quotes}
            loading={loading}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
            getFlashAnimation={getFlashAnimation}
          />
        ) : (
          // ---- EQUITY / FUTURES TABLE ----
          <>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 420px)' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {renderSortLabel('tradingsymbol', 'Symbol', 'left')}
                    {segment === 'futures' && renderSortLabel('expiry', 'Expiry', 'left')}
                    {renderSortLabel('ltp', 'LTP')}
                    {renderSortLabel('change', 'Change')}
                    {renderSortLabel('changePercent', '%Change')}
                    {renderSortLabel('open' as SortField, 'Open')}
                    {renderSortLabel('high' as SortField, 'High')}
                    {renderSortLabel('low' as SortField, 'Low')}
                    {renderSortLabel('close' as SortField, 'Prev Close')}
                    {renderSortLabel('volume', 'Volume')}
                    {segment === 'futures' && renderSortLabel('oi', 'OI')}
                    {segment === 'futures' && renderSortLabel('lot_size', 'Lot Size')}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <SkeletonRows cols={segment === 'futures' ? 12 : 9} rows={15} />
                  ) : pagedInstruments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={segment === 'futures' ? 12 : 9} align="center" sx={{ py: 8 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <ShowChartIcon sx={{ fontSize: 48, color: COLORS.textSecondary, opacity: 0.3, mb: 1 }} />
                          <Typography variant="body1" sx={{ color: COLORS.textSecondary }}>
                            {search ? 'No instruments match your search' : 'No instruments available'}
                          </Typography>
                          {!isKiteConnected && (
                            <Typography variant="body2" sx={{ color: COLORS.textSecondary, mt: 0.5 }}>
                              Connect to Kite for live instrument data
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedInstruments.map((inst) => {
                      const q = quotes[inst.tradingsymbol];
                      const flash = getFlashAnimation(inst.tradingsymbol);
                      const isPos = (q?.change ?? 0) >= 0;

                      return (
                        <TableRow
                          key={inst.tradingsymbol}
                          hover
                          sx={{
                            animation: flash,
                            '&:last-child td': { borderBottom: 0 },
                          }}
                        >
                          {/* Symbol */}
                          <TableCell sx={{ fontWeight: 600, color: COLORS.textPrimary }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {inst.tradingsymbol}
                              {q && (
                                <Chip
                                  label={inst.exchange}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    bgcolor: `${COLORS.blue}15`,
                                    color: COLORS.blue,
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>

                          {/* Expiry (Futures) */}
                          {segment === 'futures' && (
                            <TableCell sx={{ color: COLORS.textSecondary, fontSize: '0.8rem' }}>
                              {inst.expiry || '--'}
                            </TableCell>
                          )}

                          {/* LTP */}
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontFamily: MONO, fontWeight: 700, color: COLORS.textPrimary }}>
                              {q ? formatPrice(q.ltp) : '--'}
                            </Typography>
                          </TableCell>

                          {/* Change */}
                          <TableCell align="right">
                            {q ? (
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: MONO, fontWeight: 600, color: isPos ? COLORS.positive : COLORS.negative }}
                              >
                                {formatChange(q.change)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>--</Typography>
                            )}
                          </TableCell>

                          {/* %Change */}
                          <TableCell align="right">
                            {q ? (
                              <Chip
                                label={formatPercent(q.changePercent)}
                                size="small"
                                sx={{
                                  fontFamily: MONO,
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  height: 24,
                                  bgcolor: isPos ? COLORS.positiveLight : COLORS.negativeLight,
                                  color: isPos ? COLORS.positive : COLORS.negative,
                                }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>--</Typography>
                            )}
                          </TableCell>

                          {/* Open */}
                          <TableCell align="right">{priceCell(q?.open)}</TableCell>

                          {/* High */}
                          <TableCell align="right">{priceCell(q?.high)}</TableCell>

                          {/* Low */}
                          <TableCell align="right">{priceCell(q?.low)}</TableCell>

                          {/* Prev Close */}
                          <TableCell align="right">{priceCell(q?.close)}</TableCell>

                          {/* Volume */}
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontFamily: MONO, color: COLORS.textSecondary }}>
                              {q?.volume ? formatVolume(q.volume) : '--'}
                            </Typography>
                          </TableCell>

                          {/* OI (Futures) */}
                          {segment === 'futures' && (
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontFamily: MONO, color: COLORS.textSecondary }}>
                                {q?.oi ? formatVolume(q.oi) : '--'}
                              </Typography>
                            </TableCell>
                          )}

                          {/* Lot Size (Futures) */}
                          {segment === 'futures' && (
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontFamily: MONO }}>
                                {inst.lot_size || LOT_SIZE_MAP[inst.name] || '--'}
                              </Typography>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* PAGINATION */}
            <TablePagination
              component="div"
              count={filteredInstruments.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[50]}
              sx={{
                borderTop: `1px solid ${COLORS.border}`,
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  fontWeight: 500,
                  color: COLORS.textSecondary,
                },
              }}
            />
          </>
        )}

        {/* Pagination for Options too */}
        {segment === 'options' && (
          <TablePagination
            component="div"
            count={filteredInstruments.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[50]}
            sx={{
              borderTop: `1px solid ${COLORS.border}`,
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontWeight: 500,
                color: COLORS.textSecondary,
              },
            }}
          />
        )}
      </Paper>
    </Box>
  );
}

// ============================================================================
// OPTION CHAIN TABLE (sub-component)
// ============================================================================

interface OptionChainTableProps {
  instruments: Instrument[];
  quotes: Record<string, BulkQuoteData>;
  loading: boolean;
  sortField: SortField;
  sortDir: 'asc' | 'desc';
  onSort: (field: SortField) => void;
  getFlashAnimation: (symbol: string) => string | undefined;
}

function OptionChainTable({ instruments, quotes, loading, getFlashAnimation }: OptionChainTableProps) {
  // Group by strike: for each strike, find CE and PE
  const strikes = useMemo(() => {
    const map = new Map<number, { ce?: Instrument; pe?: Instrument }>();

    instruments.forEach((inst) => {
      if (inst.strike === undefined) return;
      if (!map.has(inst.strike)) map.set(inst.strike, {});
      const entry = map.get(inst.strike)!;
      if (inst.instrument_type === 'CE') entry.ce = inst;
      else if (inst.instrument_type === 'PE') entry.pe = inst;
    });

    return Array.from(map.entries())
      .sort(([a], [b]) => a - b)
      .map(([strike, { ce, pe }]) => ({ strike, ce, pe }));
  }, [instruments]);

  const optPriceCell = (inst: Instrument | undefined, field: 'ltp' | 'change' | 'oi' | 'volume') => {
    if (!inst) return <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>--</Typography>;
    const q = quotes[inst.tradingsymbol];
    if (!q) return <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>--</Typography>;

    const val = q[field];
    if (val === undefined) return <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>--</Typography>;

    if (field === 'ltp') return <Typography variant="body2" sx={{ fontFamily: MONO, fontWeight: 600 }}>{formatPrice(val as number)}</Typography>;
    if (field === 'change') {
      const isPos = (val as number) >= 0;
      return (
        <Typography variant="body2" sx={{ fontFamily: MONO, fontWeight: 600, color: isPos ? COLORS.positive : COLORS.negative }}>
          {formatChange(val as number)}
        </Typography>
      );
    }
    if (field === 'oi' || field === 'volume') {
      return <Typography variant="body2" sx={{ fontFamily: MONO, color: COLORS.textSecondary }}>{formatVolume(val as number)}</Typography>;
    }

    return <Typography variant="body2">--</Typography>;
  };

  return (
    <TableContainer sx={{ maxHeight: 'calc(100vh - 420px)' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {/* CALL side */}
            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: `${COLORS.positiveLight}`, color: COLORS.positive, borderRight: 'none' }}>
              Call LTP
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: `${COLORS.positiveLight}`, color: COLORS.positive }}>
              Call Chg
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: `${COLORS.positiveLight}`, color: COLORS.positive }}>
              Call OI
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: `${COLORS.positiveLight}`, color: COLORS.positive }}>
              Call Vol
            </TableCell>

            {/* STRIKE */}
            <TableCell
              align="center"
              sx={{
                fontWeight: 700,
                bgcolor: `${COLORS.primary}15`,
                color: COLORS.primary,
                borderLeft: `2px solid ${COLORS.border}`,
                borderRight: `2px solid ${COLORS.border}`,
                minWidth: 100,
              }}
            >
              Strike
            </TableCell>

            {/* PUT side */}
            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: `${COLORS.negativeLight}`, color: COLORS.negative }}>
              Put LTP
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: `${COLORS.negativeLight}`, color: COLORS.negative }}>
              Put Chg
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: `${COLORS.negativeLight}`, color: COLORS.negative }}>
              Put OI
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: `${COLORS.negativeLight}`, color: COLORS.negative }}>
              Put Vol
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <SkeletonRows cols={9} rows={15} />
          ) : strikes.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <ShowChartIcon sx={{ fontSize: 48, color: COLORS.textSecondary, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body1" sx={{ color: COLORS.textSecondary }}>
                    No option chain data available
                  </Typography>
                  <Typography variant="body2" sx={{ color: COLORS.textSecondary, mt: 0.5 }}>
                    Select an underlying and expiry, or connect to Kite for live data
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            strikes.map(({ strike, ce, pe }) => {
              const ceFlash = ce ? getFlashAnimation(ce.tradingsymbol) : undefined;
              const peFlash = pe ? getFlashAnimation(pe.tradingsymbol) : undefined;

              return (
                <TableRow key={strike} hover>
                  {/* CALL LTP */}
                  <TableCell align="right" sx={{ animation: ceFlash, bgcolor: `${COLORS.positiveLight}40` }}>
                    {optPriceCell(ce, 'ltp')}
                  </TableCell>
                  <TableCell align="right" sx={{ bgcolor: `${COLORS.positiveLight}40` }}>
                    {optPriceCell(ce, 'change')}
                  </TableCell>
                  <TableCell align="right" sx={{ bgcolor: `${COLORS.positiveLight}40` }}>
                    {optPriceCell(ce, 'oi')}
                  </TableCell>
                  <TableCell align="right" sx={{ bgcolor: `${COLORS.positiveLight}40` }}>
                    {optPriceCell(ce, 'volume')}
                  </TableCell>

                  {/* STRIKE */}
                  <TableCell
                    align="center"
                    sx={{
                      fontFamily: MONO,
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      color: COLORS.primary,
                      bgcolor: `${COLORS.primary}08`,
                      borderLeft: `2px solid ${COLORS.border}`,
                      borderRight: `2px solid ${COLORS.border}`,
                    }}
                  >
                    {formatNumber(strike)}
                  </TableCell>

                  {/* PUT LTP */}
                  <TableCell align="right" sx={{ animation: peFlash, bgcolor: `${COLORS.negativeLight}40` }}>
                    {optPriceCell(pe, 'ltp')}
                  </TableCell>
                  <TableCell align="right" sx={{ bgcolor: `${COLORS.negativeLight}40` }}>
                    {optPriceCell(pe, 'change')}
                  </TableCell>
                  <TableCell align="right" sx={{ bgcolor: `${COLORS.negativeLight}40` }}>
                    {optPriceCell(pe, 'oi')}
                  </TableCell>
                  <TableCell align="right" sx={{ bgcolor: `${COLORS.negativeLight}40` }}>
                    {optPriceCell(pe, 'volume')}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
